import * as React from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";

// Components
import IconButton from "@material-ui/core/IconButton";
import Avatar from "@material-ui/core/Avatar";

// Custom Components
import { CategoryItemCard } from "components";
import OptionsMenu, { MenuOption } from "../../common/OptionsMenu";
import { ConfirmationMessage } from "../../MessageBoxDialogs";
import ClickableCard from "../../common/ClickableCard";

// Models
import * as CustomLists from "../../../models/customLists";
import { Languages } from "../../../models";

// Stores
import { BrowseListsStore } from "../../../stores/BrowseListsStore";

// Localization
import { strings } from "../../../core/localizedStrings";

import { Fonts } from "../../../core/utils/Fonts";
import { Colors } from "../../../core/utils/Colors";
import * as Icons from "../../../core/utils/Icons";
import { Grid, Typography } from "@material-ui/core";
import { DefaultProfileImages } from "assets";

export interface CategoryCardProps extends React.Props<any> {
    category: CustomLists.Category;
    connectDragSource?: (source: any) => any;
    store: BrowseListsStore;
}

const styles = {
    title: {
        fontSize: 22,
        fontFamily: Fonts.Roboto,
        textAlign: "left",
        paddingTop: 5,
    } as React.CSSProperties,
    menu: {
        top: 0,
        right: 10,
        position: "absolute",
    } as React.CSSProperties,
    date: {
        bottom: 5,
        right: 15,
        position: "absolute",
    } as React.CSSProperties,
    avatar: {
        width: 20,
        height: 20,
        marginBottom: 4,
    } as React.CSSProperties,
    flag: {
        width: 20,
        height: 20,
    } as React.CSSProperties,
    icon: {
        width: 20,
        height: 20,
        marginRight: -10,
    } as React.CSSProperties,
};

@observer
export class CategoryCard extends React.Component<CategoryCardProps> {
    @observable
    // private previewCardIndex = 0;
    protected onBackgroundClicked = (event: any) => {
        if (event.currentTarget.id === "background") {
            this.props.store.setCurrentCategory(this.props.category);
        }
    };

    public render() {
        if (this.props.category !== undefined) {
            var title = this.props.category.title;
            let iconSource = Languages.imageForVoice(this.props.category.voice);
            return (
                <div style={{ position: "relative" }}>
                    <ClickableCard onClickCallback={this.onBackgroundClicked}>
                        <Grid container alignItems="center">
                            <Grid item style={{ width: 200, padding: "5px 5px" }}>
                                {this.props.category.activityItems && this.props.category.activityItems.length > 0 && (
                                    <CategoryItemCard
                                        category={this.props.category}
                                        item={this.props.category.activityItems[0]}
                                        cardHeight={60}
                                        cardWidth="100%"
                                    />
                                )}
                            </Grid>
                            <Grid item style={{ paddingLeft: 7 }}>
                                <Grid container direction="column">
                                    <Grid item style={styles.title}>
                                        {title}
                                    </Grid>
                                    <Grid item>{this.renderCardCountsAndFavorites()}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </ClickableCard>
                    <div style={styles.menu}>{this.renderMenu()}</div>
                    <div style={styles.date}>
                        <Grid container alignItems="center" justify="flex-end" spacing={1} style={{ minWidth: 180 }}>
                            <Grid item>
                                <Typography
                                    style={{ fontSize: 16, marginTop: -5, color: Colors.grey, paddingRight: 5 }}
                                >
                                    {this.props.category.created.toLocaleDateString()}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <img style={styles.flag} src={iconSource} />
                            </Grid>
                            <Grid item>
                                <Avatar
                                    src={
                                        this.props.category.profileImage
                                            ? this.props.category.profileImage
                                            : DefaultProfileImages.neutralProfileImage()
                                    }
                                    style={styles.avatar}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </div>
            );
        } else {
            return null;
        }
    }

    protected renderMenu() {
        let isFavorite =
            this.props.store.currentTab === CustomLists.TabSelection.FAVORITES ||
            this.props.category.isFavoritedBy(this.props.store.rootStore.userStore.user.uid);

        let favoriteColor = isFavorite ? "orange" : Colors.grey;
        let favoriteStyle = { color: favoriteColor };
        favoriteStyle = Object.assign(favoriteStyle, styles.icon);
        return (
            <Grid item>
                {!isFavorite ? (
                    <IconButton onClick={this.onFavoriteButtonClicked}>
                        <Icons.Favorite style={favoriteStyle} />
                    </IconButton>
                ) : (
                    <div />
                )}
                <IconButton onClick={this.onPreviewButtonClicked}>
                    <Icons.Preview style={styles.icon} />
                </IconButton>
                {this.renderMoreOptions()}
            </Grid>
        );
    }

    protected renderMoreOptions() {
        let options: MenuOption[] = [];
        if (
            this.props.store.currentTab === CustomLists.TabSelection.SITE ||
            this.props.store.currentTab === CustomLists.TabSelection.PUBLIC
        ) {
            options = [MenuOption.Copy];
        } else if (this.props.store.currentTab === CustomLists.TabSelection.FAVORITES) {
            options = [MenuOption.Move, MenuOption.Copy, MenuOption.Delete];
        } else {
            options = [MenuOption.Edit, MenuOption.Move, MenuOption.Copy, MenuOption.Delete];
        }

        if (this.props.store.rootStore.userStore.user.isAdmin) {
            options = [MenuOption.Edit, MenuOption.Move, MenuOption.Copy, MenuOption.Delete];
        }

        return <OptionsMenu options={options} onMenuSelectionChangedCallback={this.onMenuSelectionChanged} />;
    }

    @action
    private onFavoriteButtonClicked = () => {
        if (!this.props.store.rootStore.authStore.isSignedIn) {
            this.props.store.rootStore.showLoginRequiredMessage(strings.loginToFavorite);
        } else {
            this.props.store.favoriteToAdd = this.props.category;
        }
    };

    private onPreviewButtonClicked = () => {
        this.props.store.setCategoryToPreview(this.props.category);
    };

    private onMenuSelectionChanged = (menuSelection: MenuOption) => {
        switch (menuSelection) {
            case MenuOption.Edit:
                this.props.store.setItemToEdit(this.props.category);
                this.props.store.rootStore.activityStore.setShouldShowCreateListsView(true);
                break;
            case MenuOption.Delete:
                let text =
                    this.props.store.currentTab === CustomLists.TabSelection.FAVORITES
                        ? strings.deleteFavoriteConfirmation
                        : strings.deleteCategoryConfirmation;

                let message = new ConfirmationMessage(text, undefined, (result, shouldQuell) => {
                    if (result === "yes") {
                        this.props.store.deleteCategory(this.props.category);
                    }
                });
                this.props.store.rootStore.showConfirmationMessage(message);

                break;
            case MenuOption.Copy:
                if (!this.props.store.rootStore.authStore.isSignedIn) {
                    this.props.store.rootStore.showInfoMessage({ text: strings.loginToCopy });
                } else {
                    this.props.store.setItemToCopy(this.props.category);
                }
                break;
            case MenuOption.Move:
                this.props.store.setItemToMove(this.props.category);
                break;

            default:
                break;
        }
    };

    // @action
    // private onPreviewCardClicked = (event: Event) => {
    //     event.stopPropagation();
    //     if (this.previewCardIndex === Object.keys(this.props.category.items).length - 1) {
    //         this.previewCardIndex = 0;
    //     } else {
    //         this.previewCardIndex++;
    //     }
    // };

    private renderCardCountsAndFavorites() {
        if (this.props.category === undefined) {
            return null;
        }
        if (!this.props.store.showCategoryDetails) {
            return null;
        }

        let itemCount = Object.keys(this.props.category.activityItems).length;
        let cardCountText = this.props.category.activityItems.length.toString() + " ";
        cardCountText += itemCount === 1 ? strings.cardLower : strings.cardsLower;

        return (
            <Grid container alignItems="center" spacing={2} style={{ minWidth: 200 }}>
                <Grid item>
                    <Typography style={{ fontSize: 16, color: Colors.grey, textAlign: "left" }}>
                        {cardCountText}
                    </Typography>
                </Grid>
                <Grid item>
                    {this.props.category.favoritedByFolders && this.props.category.favoritedByCount > 0 && (
                        <Grid container>
                            <Grid item>
                                <Icons.Favorite style={{ color: Colors.grey, width: 15, height: 15, marginTop: 5 }} />
                            </Grid>
                            <Grid item>
                                <Typography style={{ color: Colors.grey }}>
                                    {Object.keys(this.props.category.favoritedByFolders).length}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        );
    }
}
